/** Pure view-model helpers for the class Tests tab (no React, no API). */
export { sessionHeading } from "../sessionsList/sessionsModel";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const pluralize = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "locked" wins, then enabled means "live"; a test with no flag is live. */
export function deriveStatus(test) {
  if (test?.locked) return "locked";
  return test?.enabled !== false ? "live" : "draft";
}

export const STATUS_LABEL = { live: "Live", locked: "Locked", draft: "Draft" };

const asDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};
const dayLabel = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;
const timeLabel = (d) => `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")} ${d.getHours() >= 12 ? "PM" : "AM"}`;
const stamp = (d) => `${dayLabel(d)}, ${timeLabel(d)}`;
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "Sep 22, 9:00 AM – 5:00 PM", "Opens …", "Closes …", or null when the test has no availability window. */
export function formatWindow(opensAt, closesAt) {
  const open = asDate(opensAt);
  const close = asDate(closesAt);
  if (open && close) return sameDay(open, close) ? `${stamp(open)} – ${timeLabel(close)}` : `${stamp(open)} – ${stamp(close)}`;
  if (open) return `Opens ${stamp(open)}`;
  if (close) return `Closes ${stamp(close)}`;
  return null;
}

/** passingScore is a percent: "60% to pass (30 of 50 pts)" when the point total is known, else "60% to pass". */
export function passMark(test) {
  if (test?.passingScore == null) return null;
  const pct = Number(test.passingScore);
  const total = Number.isFinite(Number(test.totalPoints)) && Number(test.totalPoints) > 0
    ? Number(test.totalPoints)
    : parseQuestions(test.questions).reduce((n, q) => n + (Number(q.points) || 0), 0);
  return total > 0 ? `${pct}% to pass (${Math.ceil((pct * total) / 100)} of ${total} pts)` : `${pct}% to pass`;
}

/** maxRetakes is the total number of attempts per student (1 = no retakes). */
export const attemptsLabel = (n) => pluralize(Math.max(1, Number(n) || 1), "attempt");

export function submissionNote(submitted, total) {
  if (!submitted) return "No submissions yet";
  return total > 0 && submitted >= total ? "All in" : "In progress";
}

/** Everything a test row shows. `progress` is { submitted, passed } for this test; `totalStudents` is the class size. */
export function buildTestRow(test, progress, totalStudents = 0) {
  const status = deriveStatus(test);
  const count = test.questionCount ?? (Array.isArray(test.questions) ? test.questions.length : 0);
  // distinct students (not attempts); falls back to the attempt count while the API only sends that
  const submitted = progress?.submittedStudents ?? progress?.submitted ?? 0;
  return {
    id: test.id,
    title: test.title,
    description: test.description || "",
    status,
    statusLabel: STATUS_LABEL[status],
    visible: test.enabled !== false,
    locked: Boolean(test.locked),
    inLearningPlan: Boolean(test.inLearningPlan),
    questionCount: count,
    summary: [
      pluralize(count, "question"),
      passMark(test) || "No pass mark",
      test.timeLimitMinutes ? `${test.timeLimitMinutes} min` : "No time limit",
      attemptsLabel(test.maxRetakes),
    ].join(" · "),
    window: formatWindow(test.opensAt, test.closesAt),
    submitted,
    passed: progress?.passedStudents ?? progress?.passed ?? 0,
    totalStudents,
    note: submissionNote(submitted, totalStudents),
  };
}

export const countByStatus = (tests = []) => ({
  all: tests.length,
  live: tests.filter((t) => deriveStatus(t) === "live").length,
  draft: tests.filter((t) => deriveStatus(t) === "draft").length,
  locked: tests.filter((t) => deriveStatus(t) === "locked").length,
});

const byOrder = (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0);

/** Tests matching the status filter and search text, in display order. Returns a new array. */
export function filterTests(tests, filter = "all", query = "") {
  const q = query.trim().toLowerCase();
  return (tests || [])
    .filter((t) => filter === "all" || deriveStatus(t) === filter)
    .filter((t) => !q || t.title?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q))
    .slice()
    .sort(byOrder);
}

function parseQuestions(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return [];
  try {
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/** Instructor answer key: each question with its options (correct flagged), matching pairs or accepted answers. */
export function answerKeyItems(test) {
  return parseQuestions(test?.questions).map((q, i) => ({
    id: q.id ?? i,
    text: q.questionText || q.text || q.question || q.criteria || "—",
    options: (q.options || q.choices || []).map((o, oi) => ({
      letter: String.fromCharCode(65 + oi),
      text: typeof o === "string" ? o : o.text || o.label || "",
      correct: typeof o === "object" && Boolean(o.isCorrect),
    })),
    pairs: (q.pairs || []).map((p) => ({ prompt: p.prompt, answer: p.answer })),
    isShort: q.type === "SHORT_ANSWER",
    accepted: q.type === "SHORT_ANSWER" ? q.acceptedAnswers || [] : null,
  }));
}
